// THIS FILE IS USED TO GENERATE CHUNKS PER PAGE
// SO THAT SPA'S CAN LOAD THE NECESSARY CHUNKS PER PAGE
/* eslint-disable no-nested-ternary */
import React from 'react';
// eslint-disable-next-line spaced-comment
//import { Generic3xx } from '@thd-nucleus/redirector';
import { generateAsyncRoute, useLocation } from '@thd-olt-component-react/router';

const IframeRedirect = () => {
  if (typeof window !== 'undefined') {
    const { pathname, search } = window.location;
    window.location.replace(`${pathname}/form${search}`);
  }
  return null;
};

const IframeMalformedRedirect = () => {
  if (typeof window !== 'undefined') {
    const { origin } = window.location;
    window.location.replace(`${origin}/services`);
  }
  return null;
};

// Legacy 301'd the bare /services/c(/) path to the homepage. Generic3xx makes this a real HTTP
// redirect during SSR (and a location.replace in the browser), keeping the query string.
// COMMENTED OUT AS THIS WILL BE OPENED AFTER ALL PAGES HAVE TRAFFIC =========
// const HomepageRedirect = () => {
//   const { search } = useLocation();
//   return React.createElement(Generic3xx, { redirectPath: `/services/${search || ''}`, redirectCode: 301 });
// };
// COMMENTED OUT AS THIS WILL BE OPENED AFTER ALL PAGES HAVE TRAFFIC =========

const HardReload = () => {
  if (typeof window !== 'undefined') {
    const navEntry = performance?.getEntriesByType?.('navigation')?.[0];
    const navType = navEntry?.type ?? performance?.navigation?.type;

    // Check if we already tried reloading to prevent an infinite loop
    // 'reload' or 1 means the page was already refreshed
    const alreadyReloaded = navType === 'reload' || navType === 1;

    if (!alreadyReloaded) {
      window.location.reload();
    }
  }
  return null;
};

const ssr = typeof window === 'undefined' || import.meta.env.SSR;

export const getServerPages = async () => {
  const Components = {};
  if (ssr) {
    Components.NationalService = (await import('./views/national/NationalService')).NationalService;
    Components.NationalForm = (await import('./views/national/NationalForm')).NationalForm;
    Components.NationalContact = (await import('./views/national/NationalContact')).NationalContact;
    Components.NationalReviews = (await import('./views/national/NationalReviews')).NationalReviews;
    Components.PageConfirmation = (await import('./pages/PageConfirmation')).default;
    Components.HubService = (await import('./views/hub/HubService')).HubService;
    Components.MicroSite = (await import('./views/microsite/MicroSite')).MicroSite;
    Components.LocalService = (await import('./views/local/LocalService')).LocalService;
    Components.LocalForm = (await import('./views/local/LocalForm')).LocalForm;
    Components.LocalReviews = (await import('./views/local/LocalReviews')).LocalReviews;
    Components.LocalProjects = (await import('./views/local/LocalProjects')).LocalProjects;
    Components.LocalCity = (await import('./views/local/LocalCity')).LocalCity;
    Components.IframeForm = (await import('./views/iframe/IframeForm')).IframeForm;
    Components.AllServices = (await import('./views/allServices/AllServices')).AllServices;
    // COMMENTED OUT AS THIS WILL BE OPENED AFTER ALL PAGES HAVE TRAFFIC =========
    // eslint-disable-next-line spaced-comment
    //Components.Homepage = (await import('./views/homepage/Homepage')).Homepage;
    Components.Pages = (await import('./views/pages/Pages')).Pages;
    Components.ApplyHdCard = (await import('./views/applyHdCard/ApplyHdCard')).ApplyHdCard;
    Components.KitchenCostValueTool = (
      await import('./views/kitchenCostValueTool/KitchenCostValueTool')
    ).KitchenCostValueTool;
    Components.ZipcodeFinder = (await import('./views/zipcodeFinder/ZipcodeFinder')).ZipcodeFinder;
  }
  return Components;
};

export const getRoutes = (currentRouteName, currentRouteComponent, serverPages = {}) => ({
  allServices: {
    name: 'AllServices',
    componentName: 'AllServices',
    chunk: () => import('./views/allServices/AllServices'),
    get component() {
      return ssr
        ? serverPages.AllServices
        : currentRouteName === 'AllServices'
          ? currentRouteComponent
          : generateAsyncRoute(() => this.chunk().then((pageModule) => ({ default: pageModule.AllServices })));
    },
    path: '/:context?/:contentPV?/services/all',
    prefetch: [],
    channel: null,
    varnish: false
  },
  zipcodeFinder: {
    name: 'ZipcodeFinder',
    componentName: 'ZipcodeFinder',
    chunk: () => import('./views/zipcodeFinder/ZipcodeFinder'),
    get component() {
      return ssr
        ? serverPages.ZipcodeFinder
        : currentRouteName === 'ZipcodeFinder'
          ? currentRouteComponent
          : generateAsyncRoute(() => this.chunk().then((pageModule) => ({ default: pageModule.ZipcodeFinder })));
    },
    path: '/:context?/:contentPV?/services/zipcode-finder',
    prefetch: [],
    channel: null,
    varnish: false,
  },
  pages: {
    name: 'Pages',
    componentName: 'Pages',
    chunk: () => import('./views/pages/Pages'),
    get component() {
      return ssr
        ? serverPages.Pages
        : currentRouteName === 'Pages'
          ? currentRouteComponent
          : generateAsyncRoute(() => this.chunk().then((pageModule) => ({ default: pageModule.Pages })));
    },
    path: '/:context?/:contentPV?/services/pages',
    prefetch: [],
    channel: null,
    varnish: false,
  },
  applyHdCard: {
    name: 'ApplyHdCard',
    componentName: 'ApplyHdCard',
    chunk: () => import('./views/applyHdCard/ApplyHdCard'),
    get component() {
      return ssr
        ? serverPages.ApplyHdCard
        : currentRouteName === 'ApplyHdCard'
          ? currentRouteComponent
          : generateAsyncRoute(() => this.chunk().then((pageModule) => ({ default: pageModule.ApplyHdCard })));
    },
    path: '/:context?/:contentPV?/services/apply-hd-card',
    prefetch: [],
    channel: null,
    varnish: false,
  },
  kitchenCostValueTool: {
    name: 'KitchenCostValueTool',
    componentName: 'KitchenCostValueTool',
    chunk: () => import('./views/kitchenCostValueTool/KitchenCostValueTool'),
    get component() {
      return ssr
        ? serverPages.KitchenCostValueTool
        : currentRouteName === 'KitchenCostValueTool'
          ? currentRouteComponent
          : generateAsyncRoute(() => this.chunk().then((pageModule) => ({ default: pageModule.KitchenCostValueTool })));
    },
    path: '/:context?/:contentPV?/services/kitchen-cost-value-tool',
    prefetch: [],
    channel: null,
    varnish: false,
  },
  nationalForm: {
    name: 'NationalForm',
    componentName: 'NationalForm',
    chunk: () => import('./views/national/NationalForm'),
    get component() {
      return ssr
        ? serverPages.NationalForm
        : currentRouteName === 'NationalForm'
          ? currentRouteComponent
          : generateAsyncRoute(() => this.chunk().then((pageModule) => ({ default: pageModule.NationalForm })));
    },
    path: '/:context?/:contentPV?/services/c/:slug/:serviceId/form',
    prefetch: [],
    channel: null,
    varnish: false
  },
  nationalContact: {
    name: 'NationalContact',
    componentName: 'NationalContact',
    chunk: () => import('./views/national/NationalContact'),
    get component() {
      return ssr
        ? serverPages.NationalContact
        : currentRouteName === 'NationalContact'
          ? currentRouteComponent
          : generateAsyncRoute(() => this.chunk().then((pageModule) => ({ default: pageModule.NationalContact })));
    },
    path: '/:context?/:contentPV?/services/c/:slug/:serviceId/contact',
    prefetch: [],
    channel: null,
    varnish: false
  },
  nationalReviews: {
    name: 'NationalReviews',
    componentName: 'NationalReviews',
    chunk: () => import('./views/national/NationalReviews'),
    get component() {
      return ssr
        ? serverPages.NationalReviews
        : currentRouteName === 'NationalReviews'
          ? currentRouteComponent
          : generateAsyncRoute(() => this.chunk().then((pageModule) => ({ default: pageModule.NationalReviews })));
    },
    path: '/:context?/:contentPV?/services/c/:slug/:serviceId/reviews/page/:page',
    prefetch: [],
    channel: null,
    varnish: false
  },
  nationalServiceConfirmation: {
    name: 'PageConfirmation',
    componentName: 'PageConfirmation',
    chunk: () => import('./pages/PageConfirmation'),
    get component() {
      return ssr
        ? serverPages.PageConfirmation
        : currentRouteName === 'PageConfirmation'
          ? currentRouteComponent
          : generateAsyncRoute(() => this.chunk().then((pageModule) => ({ default: pageModule.default })));
    },
    path: '/:context?/:contentPV?/services/c/:slug/:serviceId/confirmation/:orderId',
    prefetch: [],
    channel: null,
    varnish: false
  },
  nationalService: {
    name: 'NationalService',
    componentName: 'NationalService',
    chunk: () => import('./views/national/NationalService'),
    get component() {
      return ssr
        ? serverPages.NationalService
        : currentRouteName === 'NationalService'
          ? currentRouteComponent
          : generateAsyncRoute(() => this.chunk().then((pageModule) => ({ default: pageModule.NationalService })));
    },
    path: '/:context?/:contentPV?/services/c/:slug/:serviceId',
    prefetch: [],
    channel: null,
    varnish: false
  },
  hubService: {
    name: 'HubService',
    componentName: 'HubService',
    chunk: () => import('./views/hub/HubService'),
    get component() {
      return ssr
        ? serverPages.HubService
        : currentRouteName === 'HubService'
          ? currentRouteComponent
          : generateAsyncRoute(() => this.chunk().then((pageModule) => ({ default: pageModule.HubService })));
    },
    path: '/:context?/:contentPV?/services/h/:slug',
    prefetch: [],
    channel: null,
    varnish: false
  },
  microSite: {
    name: 'MicroSite',
    componentName: 'MicroSite',
    chunk: () => import('./views/microsite/MicroSite'),
    get component() {
      return ssr
        ? serverPages.MicroSite
        : currentRouteName === 'MicroSite'
          ? currentRouteComponent
          : generateAsyncRoute(() => this.chunk().then((pageModule) => ({ default: pageModule.MicroSite })));
    },
    path: '/:context?/:contentPV?/services/m/:slug',
    prefetch: [],
    channel: null,
    varnish: false,
  },
  localForm: {
    name: 'LocalForm',
    componentName: 'LocalForm',
    chunk: () => import('./views/local/LocalForm'),
    get component() {
      return ssr
        ? serverPages.LocalForm
        : currentRouteName === 'LocalForm'
          ? currentRouteComponent
          : generateAsyncRoute(() => this.chunk().then((pageModule) => ({ default: pageModule.LocalForm })));
    },
    path: '/:context?/:contentPV?/services/l/:state/:city/:slug/:serviceId/form',
    prefetch: [],
    channel: null,
    varnish: false
  },
  localReviews: {
    name: 'LocalReviews',
    componentName: 'LocalReviews',
    chunk: () => import('./views/local/LocalReviews'),
    get component() {
      return ssr
        ? serverPages.LocalReviews
        : currentRouteName === 'LocalReviews'
          ? currentRouteComponent
          : generateAsyncRoute(() => this.chunk().then((pageModule) => ({ default: pageModule.LocalReviews })));
    },
    path: '/:context?/:contentPV?/services/l/:state/:city/:slug/:serviceId/reviews/page/:page',
    prefetch: [],
    channel: null,
    varnish: false
  },
  localProjects: {
    name: 'LocalProjects',
    componentName: 'LocalProjects',
    chunk: () => import('./views/local/LocalProjects'),
    get component() {
      return ssr
        ? serverPages.LocalProjects
        : currentRouteName === 'LocalProjects'
          ? currentRouteComponent
          : generateAsyncRoute(() => this.chunk().then((pageModule) => ({ default: pageModule.LocalProjects })));
    },
    path: '/:context?/:contentPV?/services/l/:state/:city/:slug/:serviceId/projects/page/:page',
    prefetch: [],
    channel: null,
    varnish: false
  },
  localServiceConfirmation: {
    name: 'PageConfirmation',
    componentName: 'PageConfirmation',
    chunk: () => import('./pages/PageConfirmation'),
    get component() {
      return ssr
        ? serverPages.PageConfirmation
        : currentRouteName === 'PageConfirmation'
          ? currentRouteComponent
          : generateAsyncRoute(() => this.chunk().then((pageModule) => ({ default: pageModule.default })));
    },
    path: '/:context?/:contentPV?/services/l/:state/:city/:slug/:serviceId/confirmation/:orderId',
    prefetch: [],
    channel: null,
    varnish: false
  },
  localService: {
    name: 'LocalService',
    componentName: 'LocalService',
    chunk: () => import('./views/local/LocalService'),
    get component() {
      return ssr
        ? serverPages.LocalService
        : currentRouteName === 'LocalService'
          ? currentRouteComponent
          : generateAsyncRoute(() => this.chunk().then((pageModule) => ({ default: pageModule.LocalService })));
    },
    path: '/:context?/:contentPV?/services/l/:state/:city/:slug/:serviceId',
    prefetch: [],
    channel: null,
    varnish: false
  },
  localCity: {
    name: 'LocalCity',
    componentName: 'LocalCity',
    chunk: () => import('./views/local/LocalCity'),
    get component() {
      return ssr
        ? serverPages.LocalCity
        : currentRouteName === 'LocalCity'
          ? currentRouteComponent
          : generateAsyncRoute(() => this.chunk().then((pageModule) => ({ default: pageModule.LocalCity })));
    },
    path: '/:context?/:contentPV?/services/l/:state/:city',
    prefetch: [],
    channel: null,
    varnish: false,
  },
  iframeForm: {
    name: 'IframeForm',
    componentName: 'IframeForm',
    chunk: () => import('./views/iframe/IframeForm'),
    get component() {
      return ssr
        ? serverPages.IframeForm
        : currentRouteName === 'IframeForm'
          ? currentRouteComponent
          : generateAsyncRoute(() => this.chunk().then((pageModule) => ({ default: pageModule.IframeForm })));
    },
    path: '/:context?/:contentPV?/services/i/:slug/:serviceId/form',
    prefetch: [],
    channel: null,
    varnish: false
  },
  iframeServiceConfirmation: {
    name: 'PageConfirmation',
    componentName: 'PageConfirmation',
    chunk: () => import('./pages/PageConfirmation'),
    get component() {
      return ssr
        ? serverPages.PageConfirmation
        : currentRouteName === 'PageConfirmation'
          ? currentRouteComponent
          : generateAsyncRoute(() => this.chunk().then((pageModule) => ({ default: pageModule.default })));
    },
    path: '/:context?/:contentPV?/services/i/:slug/:serviceId/confirmation/:orderId',
    prefetch: [],
    channel: null,
    varnish: false
  },
  iframeService: {
    name: 'IframeService',
    componentName: 'IframeService',
    chunk: () => Promise.resolve({ IframeService: IframeRedirect }),
    component: IframeRedirect,
    path: '/:context?/:contentPV?/services/i/:slug/:serviceId',
    prefetch: [],
    channel: null,
    varnish: false
  },
  iframeMalformed: {
    name: 'IframeMalformed',
    componentName: 'IframeMalformed',
    chunk: () => Promise.resolve({ IframeMalformed: IframeMalformedRedirect }),
    component: IframeMalformedRedirect,
    path: '/:context?/:contentPV?/services/i/*',
    prefetch: [],
    channel: null,
    varnish: false
  },
  // COMMENTED OUT AS THIS WILL BE OPENED AFTER ALL PAGES HAVE TRAFFIC =========
  // eslint-disable-next-line spaced-comment
  /*legacyCategoryRoot: {
    name: 'LegacyCategoryRoot',
    componentName: 'LegacyCategoryRoot',
    chunk: () => Promise.resolve({ LegacyCategoryRoot: HomepageRedirect }),
    component: HomepageRedirect,
    path: '/:context?/:contentPV?/services/c',
    prefetch: [],
    channel: null,
    varnish: false
  },
  // Declared last so the more specific /services/* routes above always win.
  homepage: {
    name: 'Homepage',
    componentName: 'Homepage',
    chunk: () => import('./views/homepage/Homepage'),
    get component() {
      return ssr
        ? serverPages.Homepage
        : currentRouteName === 'Homepage'
          ? currentRouteComponent
          : generateAsyncRoute(() => this.chunk().then((pageModule) => ({ default: pageModule.Homepage })));
    },
    path: '/:context?/:contentPV?/services',
    prefetch: [],
    channel: null,
    varnish: false,
  },*/
  // COMMENTED OUT AS THIS WILL BE OPENED AFTER ALL PAGES HAVE TRAFFIC =========
  // SAFETY NET: This catches /reviews and any other unmigrated sub-paths
  unmigratedRoutes: {
    name: 'UnmigratedPath',
    componentName: 'UnmigratedPath',
    component: HardReload,
    path: '*',
    prefetch: [],
    channel: null,
    varnish: false
  }
});
